import { useCallback, useState } from "react";
import { useStores } from "stores/setup/use-store";
import * as Yup from "yup";
import { DEFAULT_ADVANCE_TIME, DEFAULT_RECEIPT_TYPES } from "../constants";
import {
  DEFAULT_MIN_ADVANCE_TIME,
  DEFAULT_MAX_ADVANCE_TIME,
} from "pages/OceanWeatherIntelligence/InsightAlert/constants/SendConfigConstants";
import { CreateAlertFormValues } from "../types";
import { conditionGroupsSchema } from "../util/conditionGroupsSchema";

export function useAlertFormData() {
  const {
    dataStores: { insightAlertStore, userConfigStore, authStore },
  } = useStores();

  const userId = authStore.userId;
  const [initialValues, setInitialValues] = useState<CreateAlertFormValues>({
    name: "",
    templateType: undefined, // User must select a risk type
    customTitle: "",
    customDescription: "",
    channels: ["email", "push_notification", "whatsapp"],
    selectedUsers: [
      {
        id: 0,
        userId,
        userName: userConfigStore.userConfig.name,
        isWhatsappAllowed: userConfigStore.userConfig.consentTermWhatsappAlert,
        isPushAllowed: true,
      },
    ],
    minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
    maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
    minimumEventDuration: 60,
    receiptType: DEFAULT_RECEIPT_TYPES,
    locationId: undefined,
    // Insight alert fields
    dashboardId: undefined,
    insightId: undefined,
  });

  const validationSchema = Yup.object()
    .shape({
      name: Yup.string(), // Optional - will be filled automatically from customTitle for event alerts
      channels: Yup.array().min(1, "channels is required"),
      conditionGroups: Yup.mixed().when([], {
        is: () => insightAlertStore.alertCategory !== "insight",
        then: () => conditionGroupsSchema,
        otherwise: () => Yup.mixed().optional(),
      }),
      minimumEventDuration: Yup.number().required("minimumEventDuration is required"),
      // For event alerts, insightId is required
      insightId: Yup.number().when([], {
        is: () => insightAlertStore.alertCategory === "insight",
        then: (schema) => schema.required("insightId is required"),
        otherwise: (schema) => schema.optional(),
      }),
      // templateType (risk selection) is required only for standalone alerts
      // For insight/event alerts, the risk type comes from the event
      templateType: Yup.string()
        .nullable()
        .when([], {
          is: () => insightAlertStore.alertCategory !== "insight",
          then: (schema) =>
            schema.test('templateType-required', 'pleaseSelectRisk', (value) => {
              return value !== undefined && value !== null && value !== '';
            }),
          otherwise: (schema) => schema.optional(),
        }),
    })
    .test(
      'location-validation',
      'Please provide location information',
      function (values: any) {
        // Skip location validation for insight/event alerts - location comes from the event
        if (insightAlertStore.alertCategory === "insight") {
          return true;
        }

        const hasAreaLocation = (values.geofence && values.geofence.length > 0) || values.locationId;

        const hasPointLocation = values.macroRegionId && values.stationRegion;

        const hasCustomPointLocation = insightAlertStore.selectedStationForAlert?.station_id === 0;

        if (!hasAreaLocation && !hasPointLocation && !hasCustomPointLocation) {
          return this.createError({
            path: 'geofence',
            message: 'Please provide location information'
          });
        }

        return true;
      }
    )
    .test(
      'advance-time-range-validation',
      'minAdvanceTime must be less than maxAdvanceTime',
      function (values: any) {
        if (values.minAdvanceTime > values.maxAdvanceTime) {
          return this.createError({
            path: 'maxAdvanceTime',
            message: 'sendConfig.minMaxValidationError'
          });
        }

        return true;
      }
    )
    .test(
      'advance-time-gap-validation',
      'Minimum 1 hour gap required',
      function (values: any) {
        const gap = values.maxAdvanceTime - values.minAdvanceTime;
        if (gap < 60) {
          return this.createError({
            path: 'maxAdvanceTime',
            message: 'sendConfig.minGapValidationError'
          });
        }

        return true;
      }
    )
    .test(
      'custom-title-required',
      'customTitle is required',
      function (values: any) {
        const hasCustomTitle = values.customTitle && values.customTitle.trim().length > 0;

        if (!hasCustomTitle) {
          return this.createError({
            path: 'customTitle',
            message: 'thisFieldRequired'
          });
        }

        return true;
      }
    );

  const resetInitialValues = useCallback(() => {
    insightAlertStore.setOperationRuleAlert(null);
    insightAlertStore.setCreateFormValues(null);
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setMacroRegionOrigin(null);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setAlertTemplates([]);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    insightAlertStore.setAreaSelectorVisible(false);
    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setSelectedStationForAlert(null);
    insightAlertStore.setOriginalStationForAlert(null);
    // Reset insight alert related fields - always use insight mode now
    insightAlertStore.setAlertCategory("insight");
    insightAlertStore.setSelectedInsight(null);
    insightAlertStore.setSelectedInsightLocationId(null);
    insightAlertStore.setLocationMenuList([]);

    setInitialValues({
      name: "",
      templateType: undefined, // User must select a risk type
      customTitle: "",
      customDescription: "",
      channels: ["email", "push_notification", "whatsapp"],
      selectedUsers: [
        {
          id: 0,
          userId,
          userName: userConfigStore.userConfig.name,
          isWhatsappAllowed:
            userConfigStore.userConfig.consentTermWhatsappAlert,
          isPushAllowed: true,
        },
      ],
      minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
      maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
      minimumEventDuration: 60,
      receiptType: DEFAULT_RECEIPT_TYPES,
      locationId: undefined,
    });
  }, [insightAlertStore, userId, userConfigStore.userConfig.name, userConfigStore.userConfig.consentTermWhatsappAlert]);

  const updateFormValue = useCallback(
    <K extends keyof CreateAlertFormValues>(
      key: K,
      value: CreateAlertFormValues[K]
    ) => {
      setInitialValues((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  return {
    initialValues: insightAlertStore.createFormValues ?? initialValues,
    validationSchema,
    resetInitialValues,
    updateFormValue,
  };
}
